// ZoomPanComponent.js

import React, { useEffect, useRef, useState, useCallback } from 'react'; // Added useEffect
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import PathDisplayComponent from './PathDisplayComponent';
import WorkspaceDisplayComponent from './WorkspaceDisplayComponent.js';
import NodeDictDisplayComponent from './NodeDictDisplayComponent.js';
import { bearingDeg, lengthPx } from './angleLock';

// A transparent SVG carrying the frame's intrinsic width/height. When a custom
// backdrop (e.g. satellite tiles) replaces the orthophoto, this keeps the <img>
// box -- and therefore the SVG overlay and every screen->source coordinate
// calculation -- byte-for-byte identical to the raster case. An SVG data URI is
// used rather than width/height attributes because it carries a real intrinsic
// ASPECT RATIO, so max-width/max-height scale it the way a photo scales.
const frameBoxSrc = (w, h) =>
  `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}"></svg>`)}`;


// Perpendicular distance from point (px, py) to the segment ax,ay → bx,by.
// Mirrors the helper in ImageEditor; duplicated locally so ZoomPanComponent's
// hit-tests don't have to import an internal from a sibling module.
const distanceToSegment = (px, py, ax, ay, bx, by) => {
  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(px - ax, py - ay);
  let t = ((px - ax) * dx + (py - ay) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
};

// Format a distance in cm as either inches (< 1 ft) or feet+inches (>= 1 ft).
// Examples: 7.5 cm → `3.0 in`, 45 cm → `1 ft 5.7 in`.
const formatDistance = (cm) => {
  const totalInches = cm / 2.54;
  if (totalInches < 12) return `${totalInches.toFixed(1)} in`;
  const feet = Math.floor(totalInches / 12);
  const inches = totalInches - feet * 12;
  return `${feet} ft ${inches.toFixed(1)} in`;
};

// Compute measurement-banner sizing in image-space (SVG) units.
//
// The SVG uses the image's pixel dimensions as its viewBox, so 1 svg-unit ≈ 1
// image px. The base font size scales inversely with `currentScale` so the
// banner stays a roughly constant size on screen across zoom levels (this is
// the same pattern the previous formula used). On top of that, if the banner
// would be wider than the measurement line itself, the font shrinks further
// so short lines stay visible alongside their label. A small floor prevents
// the label from collapsing to nothing on extremely short lines.
const computeBannerSize = (label, startPoint, endPoint, currentScale) => {
  // Image-space font size at currentScale=1. Tuned so the banner reads
  // similarly to the previous `360 / currentScale` formula on long lines but
  // can keep shrinking past zoom-in (no hard floor on the base term).
  const BASE_FONT_AT_SCALE_1 = 360;
  const MAX_BANNER_FRAC = 0.9;
  const MIN_FONT_AT_SCALE_1 = 60;

  const dx = endPoint[0] - startPoint[0];
  const dy = endPoint[1] - startPoint[1];
  const linePxLen = Math.hypot(dx, dy);

  const baseFontPx = BASE_FONT_AT_SCALE_1 / currentScale;
  const estBannerW = label.length * baseFontPx * 0.55 + baseFontPx * 0.4;
  const shrinkRatio = estBannerW > 0
    ? Math.min(1, (linePxLen * MAX_BANNER_FRAC) / estBannerW)
    : 1;
  const minFontPx = MIN_FONT_AT_SCALE_1 / currentScale;
  const fontPx = Math.max(baseFontPx * shrinkRatio, minFontPx);

  const bgW = label.length * fontPx * 0.55 + fontPx * 0.4;
  const bgH = fontPx * 1.15;
  return { fontPx, bgW, bgH };
};

const ZoomPanComponent = React.memo(({
  image,
  imageRef,
  svgRef,
  originalImageSize,
  backdrop,           // optional node OR ({currentScale}) => node, drawn in place of the raster
  drawPreview,        // { anchor, point, locked, bearing, lengthPx } while drawing a line
  showLineLabels,     // force length/bearing labels on EVERY segment (override)

  currentScale,
  setCurrentScale,
  handleImageInteraction,
  handleMouseMove: parentHandleMouseMove,
  lines,
  boundaries,
  selectedLine,
  selectedLinePoint,
  hoveredLine,
  hoveredLinePoint,
  hoveredBoundaryPoint,
  flashingLineIds,
  selectedBoundary,
  selectedBoundaryPoint,
  hoveredBoundary,
  editLinePointMode,
  editLineMode,
  deleteLineMode,
  editBoundaryPointMode,
  editBoundaryMode,
  currentMousePos,
  modes,
  currentBoundary, // Add currentBoundary here
  setCurrentBoundary, // Add setCurrentBoundary here
  isDialogOpen,
  setSelectedLine,
  setSelectedBoundary,
  displayPath,
  displayWorkspace,
  displayNodeDict,
  geoToPixel,
  showNavigationPath,
  setShowNavigationPath,
  assignTypeAndGroupMode,
  displayImage,
  debug,
  showShapefileLines,
  shapefileCollection,
  shapefileOpacity = 1,
  measurementPoints,
  completedMeasurements,
  currentMeasurement,
  selectedMeasurement,
  setSelectedMeasurement,
  hoveredMeasurement,
  repeatPreview,
  repeatSourceLine,
  gsd,
  // selectedLine,
  // setSelectedLine,
  }) => {

  const transformComponentRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [tempBoundaryPoints, setTempBoundaryPoints] = useState([]); // Track temporary boundary points
  const [selectionRect, setSelectionRect] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [spaceHeld, setSpaceHeld] = useState(false);
  // True while the user is actively dragging to pan. Drives the
  // grab/grabbing cursor swap so the pointer reflects the live action.
  const [isPanning, setIsPanning] = useState(false);

  // Dynamic minScale — clamps zoom-out so the image always covers the
  // entire viewport along its LONGEST edge. For a square image this
  // equals the viewport aspect ratio; for a rectangle it's whichever
  // axis would letterbox first at scale=1. Recomputed on image load and
  // window resize.
  //
  // Why: the <img> is CSS-laid-out `maxWidth: 100vw; maxHeight: 100vh`
  // (contain-fit at library scale=1). To reach "cover-fit" we need the
  // library scale that makes the shorter displayed axis reach the
  // longer viewport axis:
  //     coverScale = max(vw / displayedW, vh / displayedH)
  // Whichever axis already matches the viewport contributes ratio 1
  // (no-op); the other axis drives the clamp.
  // Initial fit = cover-fit (image fills viewport) — what the user sees
  // on load. The library's hard minScale floor is set lower so the user
  // can pull back PAST cover-fit and see margin around the field (useful
  // when lines extend beyond the image edges, or for a bird's-eye look).
  const ZOOM_OUT_HEADROOM = 0.5; // 0.5 = can zoom out to half the cover-fit size
  const [initialFit, setInitialFit] = useState(1);
  const [minScale, setMinScale] = useState(ZOOM_OUT_HEADROOM);
  const computeCoverScale = useCallback(() => {
    const img = imageRef.current;
    if (!img) return 1;
    const dW = img.clientWidth;
    const dH = img.clientHeight;
    if (!dW || !dH) return 1;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    return Math.max(1, vw / dW, vh / dH);
  }, [imageRef]);
  useEffect(() => {
    const sync = () => {
      const cover = computeCoverScale();
      setInitialFit(cover);
      setMinScale(cover * ZOOM_OUT_HEADROOM);
      const ref = transformComponentRef.current;
      // On initial mount and image swap, mirror the cover-fit init scale
      // into the parent so InfoBar / sizing math don't read a stale value.
      // Don't snap back on resize — let the user keep their pulled-back
      // view if they intentionally zoomed below cover-fit.
      if (typeof setCurrentScale === 'function') {
        const onscreen = ref?.instance?.transformState?.scale ?? cover;
        setCurrentScale(onscreen);
      }
    };
    // Image may already be cached/loaded when this effect runs.
    const img = imageRef.current;
    if (img && img.complete) sync();
    if (img) img.addEventListener('load', sync);
    window.addEventListener('resize', sync);
    return () => {
      if (img) img.removeEventListener('load', sync);
      window.removeEventListener('resize', sync);
    };
  }, [image, computeCoverScale, imageRef]);

  // The displayed <img> may be a downscaled version of the original for browser compatibility
  // (Safari/Chrome can't decode very large images). Geo math must use original dimensions.
  // Frame/source units per on-screen pixel, MEASURED. Label sizes must be expressed in
  // screen pixels: a synthesised satellite frame packs tens of units per pixel while an
  // orthophoto is near 1:1, so a size quoted in source units renders ~3x too small in one
  // mode and fine in the other. (Same root cause as the KML overlay stroke fix.)
  const unitsPerScreenPx = () => {
    const el = imageRef.current;
    const w = el ? el.getBoundingClientRect().width : 0;
    return w > 0 ? getSourceWidth() / w : 1;
  };
  const getSourceWidth = () => originalImageSize?.width || imageRef.current?.naturalWidth || 100;
  const getSourceHeight = () => originalImageSize?.height || imageRef.current?.naturalHeight || 100;


  const handleZoom = (scale) => {
    if (transformComponentRef.current) {
      const { setTransform, instance } = transformComponentRef.current;
      const { positionX, positionY, scale: currentScale } = instance.transformState;
      
      // Calculate new position to keep the same center point
      const newPositionX = positionX * (scale / currentScale);
      const newPositionY = positionY * (scale / currentScale);

      setTransform(newPositionX, newPositionY, scale);
    }
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      const target = event.target;
      const isTypingTarget =
        target &&
        (
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable
        );
      if (isTypingTarget) {
        return;
      }

      if (isDialogOpen) {
        // If dialog is open, don't handle panning keys
        return;
      }
  
      const { key } = event;
      const panAmount = 100 / currentScale; // Adjust panning speed
      
      if (transformComponentRef.current) {
        let deltaX = 0;
        let deltaY = 0;
  
        switch (key) {
          case 'w':
          case 's':
          case 'a':
          case 'd':
            event.preventDefault(); // Prevent default only for these keys
            break;
          default:
            return; // Exit the function for other keys
        }
  
        switch (key) {
          case 'w':
            deltaY = panAmount;
            break;
          case 'a':
            deltaX = panAmount;
            break;
          case 's':
            deltaY = -panAmount;
            break;
          case 'd':
            deltaX = -panAmount;
            break;
        }
  
        const { setTransform } = transformComponentRef.current;
        const newPosition = {
          x: position.x + deltaX,
          y: position.y + deltaY
        };
  
        setTransform(newPosition.x, newPosition.y, currentScale);
        setPosition(newPosition);
      }
    };
  
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentScale, position, isDialogOpen]); // Add isDialogOpen to dependencies

  // Safety net: clear isPanning on any global mouseup or window blur.
  // The library's onPanningStop can miss the release (race when modes flip
  // mid-drag, or when the cursor leaves the canvas), leaving the cursor
  // stuck on 'grabbing'.
  useEffect(() => {
    const clear = () => setIsPanning(false);
    window.addEventListener('mouseup', clear);
    window.addEventListener('blur', clear);
    return () => {
      window.removeEventListener('mouseup', clear);
      window.removeEventListener('blur', clear);
    };
  }, []);

  // Whenever the active mode changes, reset the panning flag so a stuck
  // 'grabbing' from a prior mode doesn't carry over.
  useEffect(() => { setIsPanning(false); }, [modes]);

  // Space held → temporary pan override in selectMode.
  useEffect(() => {
    const onKeyDown = (e) => { if (e.code === 'Space' && !e.repeat) { e.preventDefault(); setSpaceHeld(true); } };
    const onKeyUp   = (e) => { if (e.code === 'Space') setSpaceHeld(false); };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  const handleMouseMove = (event) => {
    // Use clientX and clientY from the correct event object
    const clientX = event.clientX || event.nativeEvent?.clientX;
    const clientY = event.clientY || event.nativeEvent?.clientY;
  
    if (!imageRef.current || !clientX || !clientY) return;
  
    const rect = imageRef.current.getBoundingClientRect();
    const scale = getSourceWidth() / rect.width;
    // Sub-unit precision matters: Math.floor() used to truncate here, which both
    // QUANTISED the point to the source-unit grid and biased it consistently up-left
    // (floor never rounds up). On a synthesised frame one source unit can exceed one
    // screen pixel when zoomed in, so that truncation showed up as the placed point
    // sitting off the cursor. Fractional source coordinates cost nothing downstream
    // and are what make fine placement at high zoom actually accurate.
    const x = (clientX - rect.left) * scale;
    const y = (clientY - rect.top) * scale;

    if (isSelecting) {
      setSelectionRect(prevRect => ({ ...prevRect, x2: x, y2: y }));
    }
  
    // Always call the parent's handleMouseMove with image-relative coordinates.
    // `scale` is image-pixels-per-screen-pixel (matches handleImageInteraction),
    // used to compute hit-test thresholds. `currentScale` is the zoom level,
    // passed separately for anything that cares.
    if (parentHandleMouseMove) {
      parentHandleMouseMove(event, { x, y, scale, currentScale });
    }
  };

const handleMouseDown = (event) => {
  // Handle measurement mode clicks HERE instead of in onClick
  if (modes.measureDistanceMode) {
    event.preventDefault();
    event.stopPropagation();
    
    const rect = imageRef.current.getBoundingClientRect();
    const scaleX = getSourceWidth() / rect.width;
    const scaleY = getSourceHeight() / rect.height;
    // Sub-unit precision matters: Math.floor() used to truncate here, which both
    // QUANTISED the point to the source-unit grid and biased it consistently up-left
    // (floor never rounds up). On a synthesised frame one source unit can exceed one
    // screen pixel when zoomed in, so that truncation showed up as the placed point
    // sitting off the cursor. Fractional source coordinates cost nothing downstream
    // and are what make fine placement at high zoom actually accurate.
    const x = (event.clientX - rect.left) * scaleX;
    const y = (event.clientY - rect.top) * scaleY;
    
    // Call handleImageInteraction directly with measurement click
    handleImageInteraction({ preventDefault: () => {}, stopPropagation: () => {} }, {
      clientX: event.clientX,
      clientY: event.clientY,
      target: event.target,
      preventDefault: () => {},
      stopPropagation: () => {}
    });
    return;
  }

  if (modes.editLineMode || modes.deleteLineMode || assignTypeAndGroupMode || modes.selectMode) {
    const rect = imageRef.current.getBoundingClientRect();
    const scale = getSourceWidth() / rect.width;

    // Sub-unit precision matters: Math.floor() used to truncate here, which both
    // QUANTISED the point to the source-unit grid and biased it consistently up-left
    // (floor never rounds up). On a synthesised frame one source unit can exceed one
    // screen pixel when zoomed in, so that truncation showed up as the placed point
    // sitting off the cursor. Fractional source coordinates cost nothing downstream
    // and are what make fine placement at high zoom actually accurate.
    const x = (event.clientX - rect.left) * scale;
    const y = (event.clientY - rect.top) * scale;

    // Use Line.distanceTo (perpendicular-distance hit test). `scale` here is
    // image-pixels-per-screen-pixel. Target ~20 screen-px hit radius →
    // 20 * scale image-pixels (isPointNear multiplies its arg by 10 internally,
    // so hitRadius = 2 * scale * 10 = 20 * scale image-px).
    //
    // When multiple lines fall within the threshold (common in zoomed-out
    // views with tightly packed lines), pick the geometrically NEAREST line
    // rather than the first in array order. This makes click behavior match
    // the hover highlight exactly.
    const hitRadius = 2 * scale * 10;
    let clickedLine = null;
    let bestDist = Infinity;
    for (const line of lines) {
      if (typeof line.distanceTo !== 'function') continue;
      const d = line.distanceTo([x, y]);
      if (d <= hitRadius && d < bestDist) {
        bestDist = d;
        clickedLine = line;
      }
    }
    const additive = event.shiftKey;
    if (clickedLine) {
      if (assignTypeAndGroupMode || additive) {
        setSelectedLine(prev => {
          const prevArray = Array.isArray(prev) ? prev : [];
          const isAlreadySelected = prevArray.some(line => line.id === clickedLine.id);
          if (isAlreadySelected) {
            return prevArray.filter(line => line.id !== clickedLine.id);
          } else {
            return [...prevArray, clickedLine];
          }
        });
      } else {
        setSelectedLine([clickedLine]);
      }
    } else if (modes.selectMode && completedMeasurements && completedMeasurements.length) {
      // Measurement hit-test — same pattern as lines so shift+click multi-select
      // behaves identically. Lives here (not in ImageEditor's onClick) because
      // mousedown fires before the marquee branch and before onClick, so we
      // can short-circuit the marquee start when the click lands on a
      // measurement.
      const measHitRadius = 10 * scale;
      let hitId = null;
      let bestMeas = Infinity;
      for (const m of completedMeasurements) {
        if (!m.startPoint || !m.endPoint) continue;
        const d = distanceToSegment(x, y, m.startPoint[0], m.startPoint[1], m.endPoint[0], m.endPoint[1]);
        if (d <= measHitRadius && d < bestMeas) {
          bestMeas = d;
          hitId = m.id;
        }
      }
      if (hitId !== null) {
        if (typeof setSelectedMeasurement === 'function') {
          setSelectedMeasurement(prev => {
            const prevArr = Array.isArray(prev) ? prev : (prev != null ? [prev] : []);
            if (additive) {
              return prevArr.includes(hitId)
                ? prevArr.filter(id => id !== hitId)
                : [...prevArr, hitId];
            }
            return [hitId];
          });
        }
        // Mirror line behavior: a measurement click clears line selection.
        setSelectedLine([]);
      } else if (!spaceHeld) {
        setSelectionRect({ x1: x, y1: y, x2: x, y2: y, additive });
        setIsSelecting(true);
      }
    } else if (!spaceHeld) {
      // Start marquee. Remember if shift was held so mouseup can union vs replace.
      // Skip when space is held — that gesture is a pan, not a drag-select.
      setSelectionRect({ x1: x, y1: y, x2: x, y2: y, additive });
      setIsSelecting(true);
    }
  }
};

  const handleMouseUp = (event) => {
    setTempBoundaryPoints([]);
    if (isSelecting) {
      const newSelectedLines = lines.filter(line => isLineInSelection(line, selectionRect));
      // Outer-only boundary marquee hit. Inner is derived and not editable;
      // including it in marquee selection would make it the selectedBoundary
      // and break the outer-edits-inner-follows rule.
      const outerBoundary = boundaries
        ? boundaries.find(b => !b.isInnerBoundary && isLineInSelection(b, selectionRect))
        : null;
      // Measurement marquee hit. Adapt each measurement to the {points: [...]}
      // shape isLineInSelection expects (a 2-vertex polyline of start→end).
      const measurementHits = (completedMeasurements || [])
        .filter(m => m && m.startPoint && m.endPoint)
        .filter(m => isLineInSelection({ points: [m.startPoint, m.endPoint] }, selectionRect))
        .map(m => m.id);
      const additive = selectionRect?.additive || assignTypeAndGroupMode;
      if (additive) {
        setSelectedLine(prev => {
          const prevArray = Array.isArray(prev) ? prev : [];
          const updatedSelection = [...prevArray];
          newSelectedLines.forEach(line => {
            const index = updatedSelection.findIndex(l => l.id === line.id);
            if (index === -1) updatedSelection.push(line);
            // in assignType mode toggle; in shift-additive mode we only add.
            else if (assignTypeAndGroupMode) updatedSelection.splice(index, 1);
          });
          return updatedSelection;
        });
      } else {
        setSelectedLine(newSelectedLines.length > 0 ? newSelectedLines : []);
      }
      // Boundary selection — replace mode. Marquee is a single-rect gesture
      // so picking the first outer boundary intersected is enough (typically
      // there's one outer per scene anyway).
      if (typeof setSelectedBoundary === 'function') {
        setSelectedBoundary(outerBoundary || null);
      }
      // Measurement selection — same union/replace pattern as lines. Skip
      // entirely if no setter was passed (older callers).
      if (typeof setSelectedMeasurement === 'function') {
        if (selectionRect?.additive) {
          setSelectedMeasurement(prev => {
            const prevArr = Array.isArray(prev) ? prev : (prev != null ? [prev] : []);
            const next = [...prevArr];
            measurementHits.forEach(id => {
              if (!next.includes(id)) next.push(id);
            });
            return next;
          });
        } else {
          setSelectedMeasurement(measurementHits);
        }
      }
      setIsSelecting(false);
      setSelectionRect(null);
    }
  };

  // Segment-vs-rect intersection: true if any polyline vertex is inside the
  // rect OR any segment crosses any rect edge. Handles lines that pass
  // entirely through the marquee without endpoints inside.
  const isLineInSelection = (line, rect) => {
    if (!rect || line.points.length === 0) return false;
    const { x1, y1, x2, y2 } = rect;
    const left = Math.min(x1, x2), right = Math.max(x1, x2);
    const top = Math.min(y1, y2), bottom = Math.max(y1, y2);
    const inside = ([x, y]) => x >= left && x <= right && y >= top && y <= bottom;
    if (line.points.some(inside)) return true;

    // Segment-segment intersection (proper 2D).
    const segsIntersect = (a, b, c, d) => {
      const ccw = (p, q, r) => (q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]);
      const d1 = ccw(c, d, a);
      const d2 = ccw(c, d, b);
      const d3 = ccw(a, b, c);
      const d4 = ccw(a, b, d);
      if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
          ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) return true;
      // Colinear overlap cases are rare and not worth handling for UI marquee.
      return false;
    };
    const rectEdges = [
      [[left, top], [right, top]],
      [[right, top], [right, bottom]],
      [[right, bottom], [left, bottom]],
      [[left, bottom], [left, top]],
    ];
    for (let i = 0; i < line.points.length - 1; i++) {
      const a = line.points[i];
      const b = line.points[i + 1];
      for (const [c, d] of rectEdges) {
        if (segsIntersect(a, b, c, d)) return true;
      }
    }
    return false;
  };

  // useEffect(() => {
  //   console.log("Lines updated in ZoomPanComponent:", lines);
  // }, [lines]);

  // console.log("selectedLine in ZoomPanComponent:", selectedLine);
  // console.log("lines in ZoomPanComponent:", lines);

  // Add this helper function
  const isLineSelected = (line) => {
    return selectedLine && (
      (Array.isArray(selectedLine) && selectedLine.some(l => l.id === line.id)) ||
      (!Array.isArray(selectedLine) && selectedLine.id === line.id)
    );
  };
  
  // Helper function to collect all points from all lines and filter by distance
  const getFilteredShapefilePoints = (shapefile, gsd, spacingMeters = 10) => {
    if (!gsd || !shapefile.lines) return [];
    
    const spacingInCm = spacingMeters * 100; // Convert meters to cm
    const spacingInPixels = spacingInCm / gsd; // Convert cm to pixels using GSD
    
    // Collect all points from all lines with their line metadata
    const allPoints = [];
    shapefile.lines.forEach((line, lineIndex) => {
      line.points.forEach((point, pointIndex) => {
        allPoints.push({
          coordinates: point,
          lineIndex: lineIndex,
          pointIndex: pointIndex,
          lineId: line.id
        });
      });
    });
    
    // Sort points by their position (optional - you could sort by x, then y for systematic coverage)
    // allPoints.sort((a, b) => {
    //   if (a.coordinates[0] !== b.coordinates[0]) {
    //     return a.coordinates[0] - b.coordinates[0]; // Sort by X first
    //   }
    //   return a.coordinates[1] - b.coordinates[1]; // Then by Y
    // });
    
    // Filter points based on distance
    const filteredPoints = [];
    
    if (allPoints.length > 0) {
      // Always include the first point
      filteredPoints.push(allPoints[0]);
      let lastSelectedPoint = allPoints[0].coordinates;
      
      // Check each subsequent point
      for (let i = 1; i < allPoints.length; i++) {
        const currentPoint = allPoints[i].coordinates;
        
        // Calculate distance from last selected point
        const distance = Math.sqrt(
          Math.pow(currentPoint[0] - lastSelectedPoint[0], 2) + 
          Math.pow(currentPoint[1] - lastSelectedPoint[1], 2)
        );
        
        // If distance is >= specified spacing, include this point
        if (distance >= spacingInPixels) {
          filteredPoints.push(allPoints[i]);
          lastSelectedPoint = currentPoint;
        }
      }
    }
    
    return filteredPoints;
  };

  return (
    <TransformWrapper
      ref={transformComponentRef}
      // initialScale = cover-fit so the image fills the viewport on load.
      // minScale is below cover-fit so the user can pull back further than
      // the default fit — see ZOOM_OUT_HEADROOM in computeCoverScale.
      // `key` forces a re-init when initialFit changes so the library
      // picks up the new initialScale on image swap.
      key={`tw-${initialFit.toFixed(3)}`}
      initialScale={initialFit}
      initialPositionX={0}
      initialPositionY={0}
      minScale={minScale}
      // Practically unlimited zoom-in. The old cap of 30 stopped well short of the
      // detail needed to place a point precisely on a synthesised frame, where one
      // screen pixel can still cover several centimetres of ground. Imagery stops
      // gaining detail at the tile provider's native zoom and simply upscales past
      // that, but the GEOMETRY stays exact, which is the point of zooming in this far.
      maxScale={5000}
      centerOnInit
      doubleClick={{ disabled: true }}
      // onClick={(ref, event) => {
      //   handleImageInteraction(ref, event);
      // }}
      onPanningStart={() => setIsPanning(true)}
      onPanningStop={(ref, event) => {
        setIsPanning(false);
        handleImageInteraction(ref, event);
        setPosition({ x: ref.state.positionX, y: ref.state.positionY });
      }}
      onZoom={({ state }) => {
        setCurrentScale(state.scale);
        setPosition({ x: state.positionX, y: state.positionY });
      }}
      // onZoom only fires for wheel/pinch, NOT for the programmatic zoomIn()/
      // zoomOut() behind the +/- buttons — so button zoom used to leave
      // `currentScale` stale, and every layer that sizes itself as
      // `something / currentScale` (line strokes, point handles, measurement
      // banners, the KML overlay) kept its previous on-screen thickness.
      // onTransformed catches every transform, programmatic ones included.
      // It also fires while panning, where the scale is unchanged and React
      // bails out on the identical state, so this costs nothing there.
      onTransformed={(ref, state) => {
        setCurrentScale(state.scale);
        setPosition({ x: state.positionX, y: state.positionY });
      }}
      panning={{
        disabled: modes.editLineMode || modes.deleteLineMode || assignTypeAndGroupMode || modes.measureDistanceMode || modes.editLinePointMode || modes.editBoundaryMode || modes.editBoundaryPointMode || (modes.selectMode && !spaceHeld),
      }}
    >
      {({ zoomIn, zoomOut, resetTransform, ...rest }) => (
        <React.Fragment>
          <div className="tools" style={{
            padding: '10px 0',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: '5px',
            position: 'absolute',
            top: '10px',
            left: '10px',
            zIndex: 10,
            width: '100%',
            // The row is full-width for layout, but only the inner pill is
            // interactive — let clicks in the empty space fall through to
            // the canvas so users can draw/select near the top of the image.
            pointerEvents: 'none',
          }}>
            {/* New floating zoom cluster — matches the rounded-pill styling
                of PrimaryToolbar / UtilityCluster for visual consistency.
                Each button is icon-only with a tooltip; Reset uses the
                Compass/target icon to signal "fit to frame". */}
            <div
              role="toolbar"
              aria-label="Zoom controls"
              style={{
                display: 'inline-flex',
                flexDirection: 'column',
                gap: '2px',
                padding: '4px',
                backgroundColor: 'rgba(255,255,255,0.96)',
                border: '1px solid #cfd7d0',
                borderRadius: '10px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                // Parent disables pointer events so empty space passes clicks
                // through to the canvas; the pill itself must re-enable them.
                pointerEvents: 'auto',
              }}
            >
              <button
                onClick={() => zoomIn()}
                title="Zoom in"
                style={{
                  width: '32px', height: '32px', padding: 0,
                  border: 'none', borderRadius: '6px',
                  backgroundColor: 'transparent', color: '#223',
                  cursor: 'pointer', fontSize: '1.1rem', fontWeight: 700,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#eef4ee'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >+</button>
              <button
                onClick={() => zoomOut()}
                title="Zoom out"
                style={{
                  width: '32px', height: '32px', padding: 0,
                  border: 'none', borderRadius: '6px',
                  backgroundColor: 'transparent', color: '#223',
                  cursor: 'pointer', fontSize: '1.1rem', fontWeight: 700,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#eef4ee'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >−</button>
              <div style={{ height: 1, backgroundColor: '#e4e8e4', margin: '2px 4px' }} />
              <button
                onClick={() => resetTransform()}
                title="Reset zoom / fit image"
                style={{
                  width: '32px', height: '32px', padding: 0,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  border: 'none', borderRadius: '6px',
                  backgroundColor: 'transparent', color: '#223',
                  cursor: 'pointer', fontSize: '0.95rem', fontWeight: 700,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#eef4ee'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >⌂</button>
            </div>

          </div>
          
          <TransformComponent
            wrapperStyle={{
              width: '100%',
              height: '100%',
              // Dark backdrop so any letterboxing reads as "outside the
              // map" rather than as blank canvas. Matches the App.js
              // shell color.
              backgroundColor: '#111',
              cursor: (modes.drawingMode || modes.drawingBoundaryMode || modes.measureDistanceMode) ? 'crosshair' :
                      (modes.selectMode && spaceHeld) ? (isPanning ? 'grabbing' : 'grab') :
                      (modes.editLineMode || modes.editLinePointMode || modes.deleteLineMode || assignTypeAndGroupMode) ? 'pointer' :
                      modes.selectMode ? 'default' :
                      // Pan is the default neutral state (no mode flag set).
                      // Open-hand normally, closed-hand while dragging.
                      isPanning ? 'grabbing' : 'grab'
            }}
            contentStyle={{
              width: '100%',
              height: '100%',
            }}
          >
            <div
              onClick={(event) => {
                // Skip onClick for measurement mode since it's handled in onMouseDown
                if (modes.measureDistanceMode) {
                  event.preventDefault();
                  event.stopPropagation();
                  return;
                }
                handleImageInteraction(rest, event);
              }}
              onMouseMove={handleMouseMove}
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
              style={{
                width: '100%',
                height: '100%',
                cursor: (modes.drawingMode || modes.drawingBoundaryMode || modes.measureDistanceMode) ? 'crosshair' :
                        (modes.selectMode && spaceHeld) ? (isPanning ? 'grabbing' : 'grab') :
                        (modes.editLineMode || modes.editLinePointMode || modes.deleteLineMode || assignTypeAndGroupMode) ? 'pointer' :
                        modes.selectMode ? 'default' :
                        // Pan mode (default neutral): open-hand normally, closed-hand while dragging.
                        isPanning ? 'grabbing' : 'grab',
                position: 'relative'
              }}
            >
            <div style={{
                  position: 'relative',
                  display: 'inline-block',
                  // Debug red border removed — the fullscreen chrome makes
                  // it unnecessary and visually noisy.
                }}>
            <img
                src={backdrop && originalImageSize
                  ? frameBoxSrc(originalImageSize.width, originalImageSize.height)
                  : image}
                alt="Uploaded"
                ref={imageRef}
                style={{
                  display: 'block',
                  // Fit longest edge to container so aspect ratio is
                  // preserved regardless of whether the TIFF is landscape
                  // or portrait. The parent TransformComponent handles
                  // zoom-around-cursor from there.
                  maxWidth: '100vw',
                  maxHeight: '100vh',
                  // With a custom backdrop this element is an invisible spacer
                  // that only defines the box; the backdrop paints behind it.
                  opacity: backdrop ? 0 : (displayImage ? 1 : 0.01),
                }}
              />

              {backdrop && (
                <div style={{
                  position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                  zIndex: 0, pointerEvents: 'none',
                }}>
                  {/* A function backdrop receives the live zoom so it can size
                      strokes the same way the editor's own layers do. */}
                  {typeof backdrop === 'function'
                    ? backdrop({ currentScale, position })
                    : backdrop}
                </div>
              )}

              <svg
                ref={svgRef}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                }}
                viewBox={`0 0 ${getSourceWidth()} ${getSourceHeight()}`}
              >
            {/* Render shapefile lines when debug is enabled */}
            {showShapefileLines && shapefileCollection && shapefileCollection.filter(sf => sf.visible !== false).map((shapefile, shapefileIndex) => {
              // Get filtered points for this entire shapefile
              const filteredPoints = getFilteredShapefilePoints(shapefile, gsd, 20);
              
              return (
                <React.Fragment key={`shapefile-${shapefile.id}`}>
                  {/* Render all lines */}
                  {shapefile.lines.map((line, lineIndex) => (
                    <React.Fragment key={`shapefile-line-${shapefile.id}-${line.id || lineIndex}`}>
                      {/* Render the line with shapefile-specific color */}
                      <polyline
                        points={line.points.map(point => `${point[0]},${point[1]}`).join(' ')}
                        fill="none"
                        stroke={shapefile.color}
                        strokeWidth={Math.max(10, 50 / currentScale)}
                        strokeDasharray={`${Math.max(30, 50 / currentScale)},${Math.max(10, 18 / currentScale)}`}
                        opacity={0.75 * shapefileOpacity}
                      />
                    </React.Fragment>
                  ))}
                  
                  {/* Render filtered points as circles - these are now distributed across ALL lines */}
                  {filteredPoints.map((pointData, index) => (
                    <circle
                      key={`shapefile-filtered-point-${shapefile.id}-${pointData.lineId}-${pointData.pointIndex}-${index}`}
                      cx={pointData.coordinates[0]}
                      cy={pointData.coordinates[1]}
                      r={Math.max(12, 40 / currentScale)}
                      fill={shapefile.color}
                      stroke="white"
                      strokeWidth={Math.max(1, 2 / currentScale)}
                      opacity={0.9 * shapefileOpacity}
                    />
                  ))}
                  
                </React.Fragment>
              );
            })}                
                {displayPath && showNavigationPath && (
                  <PathDisplayComponent
                    path={displayPath}
                    imageWidth={getSourceWidth()}
                    imageHeight={getSourceHeight()}
                    scale={currentScale}
                    geoToPixel={geoToPixel}
                    debug={debug}
                  />
                )}
                <WorkspaceDisplayComponent
                  workspace={displayWorkspace}
                  imageWidth={getSourceWidth()}
                  imageHeight={getSourceHeight()}
                  scale={currentScale}
                  geoToPixel={geoToPixel}
                />
                {debug && <NodeDictDisplayComponent
                  nodeDict={displayNodeDict}
                  imageWidth={getSourceWidth()}
                  imageHeight={getSourceHeight()}
                  scale={currentScale}
                    geoToPixel={geoToPixel}
                  />}
                {tempBoundaryPoints.length > 0 && (
                  <polygon
                    points={tempBoundaryPoints.map(([x, y]) => `${x},${y}`).join(' ')}
                    fill="rgba(255, 0, 0, 0.2)"
                    stroke="red"
                    strokeWidth={2}
                  />
                )}
                {lines.map((line, index) => {
                  const inMarquee = isSelecting && selectionRect && isLineInSelection(line, selectionRect);
                  return (
                  <React.Fragment key={`line-${line.id}`}>
                    {debug && line.renderSVG(
                      currentScale,
                      isLineSelected(line) && !modes.editLinePointMode && !(modes.selectMode && selectedLinePoint),
                      (line === hoveredLine && (modes.editLineMode || modes.deleteLineMode || modes.assignTypeAndGroupMode || modes.selectMode)) || inMarquee,
                      isLineSelected(line) && (modes.editLineMode || modes.deleteLineMode || modes.assignTypeAndGroupMode),
                      0.75,
                      flashingLineIds && flashingLineIds.has && flashingLineIds.has(line.id)
                    )}
                    {/* ── Persistent per-segment length + bearing ────────────────────
                        The drawing preview only labels the segment being drawn while the
                        angle-lock modifier is held. This labels FINISHED geometry, so the
                        operator can audit a layout after the fact rather than having to
                        re-draw to see a number.

                        Own toggle, deliberately not folded into `debug`: these are
                        operational figures the operator reads, not diagnostics.

                        Only segments with enough room on screen are labelled -- a short
                        segment would just stack unreadable chips on top of each other. */}
                    {(showLineLabels || line === hoveredLine) && line.points.slice(1).map((p1, i) => {
                      const p0 = line.points[i];
                      const lenPx = lengthPx(p0, p1);
                      // currentScale is image-px per screen-px, so this is the on-screen length.
                      const onScreen = lenPx / Math.max(currentScale, 1e-6);
                      if (onScreen < 55) return null;   // no room; skip rather than overlap
                      const metres = (typeof gsd === 'number' && gsd > 0) ? (lenPx * gsd / 100) : null;
                      const brg = bearingDeg(p0, p1);
                      const txt = `${metres !== null ? metres.toFixed(1) + ' m' : Math.round(lenPx) + ' px'}  \u00b7  ${Math.round(brg)}\u00b0`;
                      const fs = 13 * unitsPerScreenPx();   // ~13 screen px in both modes
                      const tw = txt.length * fs * 0.55, th = fs * 1.45;
                      const mx = (p0[0] + p1[0]) / 2, my = (p0[1] + p1[1]) / 2;
                      return (
                        <g key={`lbl-${line.id}-${i}`} pointerEvents="none">
                          <rect x={mx - tw / 2} y={my - th / 2} width={tw} height={th}
                                rx={fs * 0.25} fill="#0b1220" opacity="0.82"
                                stroke="#8fd3ff" strokeWidth={Math.max(1, 3 / currentScale)} />
                          <text x={mx} y={my} fontSize={fs} fontWeight="600" fill="#cfe9ff"
                                textAnchor="middle" dominantBaseline="middle">{txt}</text>
                        </g>
                      );
                    })}
                    {debug && line.points.map((point, pointIndex) => {
                      const isHovered = (modes.editLinePointMode || modes.selectMode) && hoveredLinePoint && hoveredLinePoint[0] === point[0] && hoveredLinePoint[1] === point[1];
                      const isSelected = selectedLinePoint && selectedLinePoint[0] === point[0] && selectedLinePoint[1] === point[1];
                      // Scale the point marker in image-px so it stays roughly
                      // constant screen size across zooms. Hover/selected are
                      // ~3x bigger with a thicker stroke for strong visibility,
                      // especially on zoomed-out views.
                      const baseSize = Math.max(10, 50 / currentScale);
                      const size = (isHovered || isSelected) ? baseSize * 3 : baseSize;
                      const strokeW = Math.max(3, (isHovered || isSelected ? 25 : 10) / currentScale);
                      return (
                      <rect
                        key={`point-${line.id}-${pointIndex}`}
                        x={point[0] - size / 2}
                        y={point[1] - size / 2}
                        width={size}
                        height={size}
                        fill={isSelected ? 'rgba(255,102,0,0.85)' : (isHovered ? 'rgba(128,0,128,0.35)' : 'transparent')}
                        strokeWidth={strokeW}
                        stroke={isSelected ? '#ff6600' : (isHovered ? 'purple' : 'red')}
                      />
                      );
                    })}
                    {/* display line index at the start of the line */}
                    {/* {line.points.length > 0 && (
                        // Start of Selection
                        <g>
                          <circle
                            cx={line.points[0][0]}
                            cy={line.points[0][1]}
                            r={20}
                            fill="black"
                          />
                          <text
                            x={line.points[0][0]}
                            y={line.points[0][1]}
                            fill="white"
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fontSize={Math.max(36, 48 / currentScale)}
                              // Start of Selection
                              >
                                {line.id.toString().split('-')[0].substring(0, 2)}
                          </text>
                        </g>
                    )} */}
                  </React.Fragment>
                  );
                })}
                
                {boundaries.map((boundary, index) => {
                  const inMarquee = !boundary.isInnerBoundary && isSelecting && selectionRect && isLineInSelection(boundary, selectionRect);
                  return (
                  <React.Fragment key={`boundary-${index}`}>
                    {boundary.renderSVG(currentScale,
                      boundary === selectedBoundary,
                      boundary === hoveredBoundary || inMarquee,
                      false)}
                    {/* {innerBoundaries[index] && (
                      <polygon
                        points={innerBoundaries[index].map(([x, y]) => `${x},${y}`).join(' ')}
                        fill="rgba(255, 0, 0, 0.1)"
                        stroke="red"
                        strokeWidth={Math.max(2, 10 / currentScale)}
                        strokeDasharray="5,5"
                      />
                    )} */}
                    {/* Boundary-point handles. Only render on the OUTER —
                        inner is derived and not editable. Visible in
                        boundary-edit modes AND in selectMode so the user
                        gets visual feedback while picking points. Color/
                        size language matches line points (orange selected,
                        purple hovered, red default) for consistency. */}
                    {!boundary.isInnerBoundary && debug && (editBoundaryPointMode || editBoundaryMode || modes.selectMode) && boundary.points.map((point, pointIndex) => {
                      const isSelected = selectedBoundaryPoint && point[0] === selectedBoundaryPoint[0] && point[1] === selectedBoundaryPoint[1];
                      const isHovered = (editBoundaryPointMode || modes.selectMode) && hoveredBoundaryPoint && point[0] === hoveredBoundaryPoint[0] && point[1] === hoveredBoundaryPoint[1];
                      const baseR = Math.max(10, 50 / currentScale);
                      const r = (isHovered || isSelected) ? baseR * 3 : baseR;
                      const sw = Math.max(3, (isHovered || isSelected ? 25 : 10) / currentScale);
                      return (
                        <circle
                          key={`point-${pointIndex}`}
                          cx={point[0]}
                          cy={point[1]}
                          r={r}
                          fill={isSelected ? 'rgba(255,102,0,0.85)' : (isHovered ? 'rgba(128,0,128,0.35)' : 'transparent')}
                          stroke={isSelected ? '#ff6600' : (isHovered ? 'purple' : 'red')}
                          strokeWidth={sw}
                        />
                      );
                    })}
                  </React.Fragment>
                  );
                })}

                {/* {modes.drawingMode && currentMousePos && (
                  <>
                    {lines.length > 0 && lines[lines.length - 1].points.length > 0 && (
                      <line
                        x1={lines[lines.length - 1].points[lines[lines.length - 1].points.length - 1][0]}
                        y1={lines[lines.length - 1].points[lines[lines.length - 1].points.length - 1][1]}
                        x2={currentMousePos.x}
                        y2={currentMousePos.y}
                        stroke="red"
                        strokeWidth={Math.max(2, 10 / currentScale)}
                        strokeDasharray="5,5"
                      />
                    )}
                    <circle
                      cx={currentMousePos.x}
                      cy={currentMousePos.y}
                      r={Math.max(2, 60 / currentScale)}
                      fill="red"
                      opacity="1"
                    />
                  </>
                )} */}
                {modes.drawingBoundaryMode && currentBoundary && currentMousePos && (
                  <>
                  <polygon
                    points={currentBoundary.points.map(([x, y]) => `${x},${y}`).join(' ')}
                    fill="rgba(255, 0, 0, 0.1)"
                    stroke="black"
                    strokeWidth={Math.max(2, 10 / currentScale)}
                  />
                  <line
                    x1={currentBoundary.points[currentBoundary.points.length - 1][0]}
                    y1={currentBoundary.points[currentBoundary.points.length - 1][1]}
                    x2={currentMousePos.x}
                    y2={currentMousePos.y}
                    stroke="red"
                    strokeWidth={Math.max(2, 10 / currentScale)}
                  />
                  <circle
                    cx={currentMousePos.x}
                    cy={currentMousePos.y}
                    r={Math.max(2, 60 / currentScale)}
                    fill="red"
                    opacity="1"
                  />
                  </>
                )}
                {selectionRect && (
                  <rect
                    x={Math.min(selectionRect.x1, selectionRect.x2)}
                    y={Math.min(selectionRect.y1, selectionRect.y2)}
                    width={Math.abs(selectionRect.x2 - selectionRect.x1)}
                    height={Math.abs(selectionRect.y2 - selectionRect.y1)}
                    fill="rgba(255, 0, 0, 0.25)"
                    stroke="red"
                    strokeWidth={10 / currentScale}
                  />
                )}
                {/* Repeat Line direction arrow. Rendered while the Repeat
                    dialog is open. Points perpendicular to the source line
                    in the chosen direction — same perpendicular convention
                    as generateParallelLines() in utils.js. */}
                {repeatPreview && repeatSourceLine && repeatSourceLine.points && repeatSourceLine.points.length >= 2 && (() => {
                  const pts = repeatSourceLine.points;
                  const a = pts[0];
                  const b = pts[pts.length - 1];
                  const mx = (a[0] + b[0]) / 2;
                  const my = (a[1] + b[1]) / 2;
                  const dx = b[0] - a[0];
                  const dy = b[1] - a[1];
                  const lineLen = Math.hypot(dx, dy) || 1;
                  // Perpendicular (matches getPerpendicular): [-dy, dx].
                  // direction === -1 flips it, matching generateParallelLines.
                  const sign = repeatPreview.direction === -1 ? -1 : 1;
                  const perpX = (-dy / lineLen) * sign;
                  const perpY = ( dx / lineLen) * sign;
                  // Arrow length scales with zoom — stays visually constant.
                  const arrowLen = Math.max(140, 700 / currentScale);
                  const tipX = mx + perpX * arrowLen;
                  const tipY = my + perpY * arrowLen;
                  const headLen = arrowLen * 0.35;
                  // Arrow head: two short segments at ±30° off the shaft.
                  const ang = Math.atan2(perpY, perpX);
                  const a1 = ang + Math.PI - Math.PI / 6;
                  const a2 = ang + Math.PI + Math.PI / 6;
                  const sw = Math.max(8, 36 / currentScale);
                  const color = '#ff6600';
                  return (
                    <g>
                      <line x1={mx} y1={my} x2={tipX} y2={tipY} stroke={color} strokeWidth={sw} strokeLinecap="round" />
                      <line x1={tipX} y1={tipY} x2={tipX + Math.cos(a1) * headLen} y2={tipY + Math.sin(a1) * headLen} stroke={color} strokeWidth={sw} strokeLinecap="round" />
                      <line x1={tipX} y1={tipY} x2={tipX + Math.cos(a2) * headLen} y2={tipY + Math.sin(a2) * headLen} stroke={color} strokeWidth={sw} strokeLinecap="round" />
                    </g>
                  );
                })()}
                {lines.length > 0 && lines[lines.length - 1].points.length > 0 &&
                (
                  <>
                  <text
                    x={lines[0].points[0][0]}
                    y={lines[0].points[0][1]}
                    fontSize={Math.max(24, 360 / currentScale)}
                    fill="white"
                    stroke="black"
                    strokeWidth={Math.max(1, 10 / currentScale)}
                    paintOrder="stroke"
                    textAnchor="middle"
                    dominantBaseline="hanging"
                  >
                    A
                  </text>
                  <text
                    x={lines[lines.length - 1].points[lines[lines.length - 1].points.length - 1][0]}
                    y={lines[lines.length - 1].points[lines[lines.length - 1].points.length - 1][1]}
                    fontSize={Math.max(24, 360 / currentScale)}
                    fill="white"
                    stroke="black"
                    strokeWidth={Math.max(1, 10 / currentScale)}
                    paintOrder="stroke"
                    textAnchor="middle"
                    dominantBaseline="hanging"
                  >
                    B
                  </text>
                                </>
                              )}  

                {/* Measurement functionality rendering */}
                {/* Always render completed measurements */}
                {completedMeasurements && completedMeasurements.map((measurement) => {
                  const isSelected = Array.isArray(selectedMeasurement)
                    ? selectedMeasurement.includes(measurement.id)
                    : selectedMeasurement === measurement.id;
                  const isHovered  = hoveredMeasurement  === measurement.id;
                  // Live marquee preview: highlight measurements that the
                  // current drag rectangle would catch on mouseup, mirroring
                  // the hover style so the user can see what they're about
                  // to grab.
                  const inMarquee = isSelecting && selectionRect && measurement.startPoint && measurement.endPoint
                    && isLineInSelection({ points: [measurement.startPoint, measurement.endPoint] }, selectionRect);
                  // Color priority: selected > marquee/hover > default blue.
                  const stroke = isSelected ? '#ff6600' : ((isHovered || inMarquee) ? 'magenta' : 'blue');
                  const strokeW = Math.max(2, 10 / currentScale) * (isSelected || isHovered ? 1.8 : 1);
                  const label = formatDistance(measurement.distance);
                  const { fontPx, bgW, bgH } = computeBannerSize(
                    label,
                    measurement.startPoint,
                    measurement.endPoint,
                    currentScale,
                  );
                  // Offset the banner perpendicular to the line so it never
                  // overlaps the line itself. Direction is chosen so "above"
                  // means upward in image-space (smaller y) for any line angle.
                  const lineDx = measurement.endPoint[0] - measurement.startPoint[0];
                  const lineDy = measurement.endPoint[1] - measurement.startPoint[1];
                  const lineLen = Math.hypot(lineDx, lineDy) || 1;
                  let perpX = -lineDy / lineLen;
                  let perpY = lineDx / lineLen;
                  if (perpY > 0) { perpX = -perpX; perpY = -perpY; }
                  const bannerGap = bgH / 2 + strokeW / 2 + Math.max(2, 10 / currentScale);
                  const mx = (measurement.startPoint[0] + measurement.endPoint[0]) / 2 + perpX * bannerGap;
                  const my = (measurement.startPoint[1] + measurement.endPoint[1]) / 2 + perpY * bannerGap;
                  return (
                  <React.Fragment key={`measurement-${measurement.id}`}>
                    {/* Plus marker at start point */}
                    <g>
                      <line
                        x1={measurement.startPoint[0] - Math.max(10, 50 / currentScale)}
                        y1={measurement.startPoint[1]}
                        x2={measurement.startPoint[0] + Math.max(10, 50 / currentScale)}
                        y2={measurement.startPoint[1]}
                        stroke={stroke}
                        strokeWidth={strokeW}
                      />
                      <line
                        x1={measurement.startPoint[0]}
                        y1={measurement.startPoint[1] - Math.max(10, 50 / currentScale)}
                        x2={measurement.startPoint[0]}
                        y2={measurement.startPoint[1] + Math.max(10, 50 / currentScale)}
                        stroke={stroke}
                        strokeWidth={strokeW}
                      />
                    </g>

                    {/* Plus marker at end point */}
                    <g>
                      <line
                        x1={measurement.endPoint[0] - Math.max(10, 50 / currentScale)}
                        y1={measurement.endPoint[1]}
                        x2={measurement.endPoint[0] + Math.max(10, 50 / currentScale)}
                        y2={measurement.endPoint[1]}
                        stroke={stroke}
                        strokeWidth={strokeW}
                      />
                      <line
                        x1={measurement.endPoint[0]}
                        y1={measurement.endPoint[1] - Math.max(10, 50 / currentScale)}
                        x2={measurement.endPoint[0]}
                        y2={measurement.endPoint[1] + Math.max(10, 50 / currentScale)}
                        stroke={stroke}
                        strokeWidth={strokeW}
                      />
                    </g>

                    {/* Solid line between points */}
                    <line
                      x1={measurement.startPoint[0]}
                      y1={measurement.startPoint[1]}
                      x2={measurement.endPoint[0]}
                      y2={measurement.endPoint[1]}
                      stroke={stroke}
                      strokeWidth={strokeW}
                    />
                    <rect
                      x={mx - bgW / 2}
                      y={my - bgH / 2}
                      width={bgW}
                      height={bgH}
                      fill="white"
                      stroke={isSelected ? '#ff6600' : (isHovered ? 'magenta' : 'black')}
                      strokeWidth={isSelected || isHovered
                        ? Math.max(4, 20 / currentScale)
                        : Math.max(1, 3 / currentScale)}
                      rx="6"
                      opacity="0.95"
                    />

                    {/* Distance label at midpoint */}
                    <text
                      x={mx}
                      y={my}
                      fontSize={fontPx}
                      fill="black"
                      textAnchor="middle"
                      dominantBaseline="middle"
                    >
                      {label}
                    </text>

                  </React.Fragment>
                  );
                })}
                
                {/* Render current measurement (dashed line with live distance) only when in measure mode */}
                {modes.measureDistanceMode && currentMeasurement && currentMousePos && (
                  <>
                    {/* Plus marker at start point */}
                    <g>
                      <line
                        x1={currentMeasurement.startPoint[0] - Math.max(10, 50 / currentScale)}
                        y1={currentMeasurement.startPoint[1]}
                        x2={currentMeasurement.startPoint[0] + Math.max(10, 50 / currentScale)}
                        y2={currentMeasurement.startPoint[1]}
                        stroke="red"
                        strokeWidth={Math.max(2, 10 / currentScale)}
                      />
                      <line
                        x1={currentMeasurement.startPoint[0]}
                        y1={currentMeasurement.startPoint[1] - Math.max(10, 50 / currentScale)}
                        x2={currentMeasurement.startPoint[0]}
                        y2={currentMeasurement.startPoint[1] + Math.max(10, 50 / currentScale)}
                        stroke="red"
                        strokeWidth={Math.max(2, 10 / currentScale)}
                      />
                    </g>
                    
                    {/* Dashed line from start point to current mouse position */}
                    <line
                      x1={currentMeasurement.startPoint[0]}
                      y1={currentMeasurement.startPoint[1]}
                      x2={currentMousePos.x}
                      y2={currentMousePos.y}
                      stroke="red"
                      strokeWidth={Math.max(2, 10 / currentScale)}
                      strokeDasharray={`${Math.max(10, 50 / currentScale)},${Math.max(5, 25 / currentScale)}`}
                    />
                    {(() => {
                      const pixelDistance = Math.sqrt(
                        Math.pow(currentMousePos.x - currentMeasurement.startPoint[0], 2) +
                        Math.pow(currentMousePos.y - currentMeasurement.startPoint[1], 2)
                      );
                      const label = formatDistance(pixelDistance * gsd);
                      const { fontPx, bgW, bgH } = computeBannerSize(
                        label,
                        currentMeasurement.startPoint,
                        [currentMousePos.x, currentMousePos.y],
                        currentScale,
                      );
                      const lineDx = currentMousePos.x - currentMeasurement.startPoint[0];
                      const lineDy = currentMousePos.y - currentMeasurement.startPoint[1];
                      const lineLen = Math.hypot(lineDx, lineDy) || 1;
                      let perpX = -lineDy / lineLen;
                      let perpY = lineDx / lineLen;
                      if (perpY > 0) { perpX = -perpX; perpY = -perpY; }
                      const strokeW = Math.max(2, 10 / currentScale);
                      const bannerGap = bgH / 2 + strokeW / 2 + Math.max(2, 10 / currentScale);
                      const mx = (currentMeasurement.startPoint[0] + currentMousePos.x) / 2 + perpX * bannerGap;
                      const my = (currentMeasurement.startPoint[1] + currentMousePos.y) / 2 + perpY * bannerGap;
                      return (
                        <>
                          <rect
                            x={mx - bgW / 2}
                            y={my - bgH / 2}
                            width={bgW}
                            height={bgH}
                            fill="white"
                            stroke="black"
                            strokeWidth="1"
                            rx="6"
                            opacity="0.95"
                          />
                          <text
                            x={mx}
                            y={my}
                            fontSize={fontPx}
                            fill="black"
                            textAnchor="middle"
                            dominantBaseline="middle"
                          >
                            {label}
                          </text>
                        </>
                      );
                    })()}
                  </>
                )}

                {/* ── Drawing preview (rubber band) ──────────────────────────────────
                    Shows where the next point will land BEFORE the click commits it — the
                    same affordance boundary drawing already has. With the angle-lock
                    modifier held (Shift) the band turns solid amber and carries a
                    bearing/length readout, so the constraint is visible rather than
                    something you infer afterwards.
                    Sized in image-px against currentScale so it stays a constant on-screen
                    size at any zoom. [ADR: docs/20260819_docking_path_types.html] */}
                {drawPreview && (() => {
                  const { anchor, point, locked, bearing, lengthPx: len } = drawPreview;
                  const w = Math.max(2, 10 / currentScale);
                  const col = locked ? '#ffb300' : '#ffffff';
                  const dash = locked ? null : `${Math.max(10, 70 / currentScale)},${Math.max(6, 45 / currentScale)}`;
                  const fs = 15 * unitsPerScreenPx();   // ~15 screen px in both modes
                  // gsd is cm/px, so px * gsd / 100 = metres.
                  const metres = (typeof gsd === 'number' && gsd > 0) ? (len * gsd / 100) : null;
                  // Always labelled while drawing -- knowing the length and bearing BEFORE
                  // committing the click is the point. Angle-lock only changes the styling
                  // (solid amber vs dashed white), not whether the numbers are shown.
                  const label = `${Math.round(bearing)}\u00b0  \u00b7  ${metres !== null ? metres.toFixed(1) + ' m' : Math.round(len) + ' px'}`;
                  const mx = (anchor[0] + point[0]) / 2, my = (anchor[1] + point[1]) / 2;
                  const tw = label ? label.length * fs * 0.58 : 0;
                  return (
                    <g pointerEvents="none">
                      <line x1={anchor[0]} y1={anchor[1]} x2={point[0]} y2={point[1]}
                            stroke="#000" strokeWidth={w * 2.2} strokeOpacity="0.35"
                            strokeDasharray={dash || undefined} />
                      <line x1={anchor[0]} y1={anchor[1]} x2={point[0]} y2={point[1]}
                            stroke={col} strokeWidth={w * 1.3} strokeDasharray={dash || undefined} />
                      <circle cx={point[0]} cy={point[1]} r={w * 2.2} fill={col}
                              stroke="#000" strokeWidth={w * 0.5} />
                      {label && (
                        <>
                          <rect x={mx - tw / 2} y={my - fs * 1.9} width={tw} height={fs * 1.5}
                                rx={fs * 0.25} fill={locked ? '#241a00' : '#101418'} stroke={col}
                                strokeWidth={w * 0.6} opacity="0.97" />
                          <text x={mx} y={my - fs * 1.15} fontSize={fs} fontWeight="bold"
                                fill={locked ? '#ffd98a' : '#eaf2ff'} textAnchor="middle"
                                dominantBaseline="middle">{label}</text>
                        </>
                      )}
                    </g>
                  );
                })()}

              </svg>
              </div>
            </div>
          </TransformComponent>
          
          
        </React.Fragment>
      )}
    </TransformWrapper>
  );
});

export default ZoomPanComponent;